import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Sidebar from "./components/Sidebar";
import SubscriptionGuard from "./components/SubscriptionGuard";
import TrialBanner from "./components/TrialBanner";

// Pages
import LoginPage from "./pages/LoginPage";
import ActivatePage from "./pages/ActivatePage";
import SubscriptionPage from "./pages/SubscriptionPage";
import AdminSubscriptionsPage from "./pages/AdminSubscriptionsPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminCompaniesPage from "./pages/AdminCompaniesPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import PublicReservationPage from "./pages/PublicReservationPage";
import PublicHandbookPage from "./pages/PublicHandbookPage";
import PublicSnelscanPage from "./pages/PublicSnelscanPage";
import PublicOverviewPage from "./pages/PublicOverviewPage";
import DashboardPage from "./pages/DashboardPage";
import OmzetVerkoopPage from "./pages/OmzetVerkoopPage";
import KostenMargesPage from "./pages/KostenMargesPage";
import VoorraadInkoopPage from "./pages/VoorraadInkoopPage";
import PersoneelPlanningPage from "./pages/PersoneelPlanningPage";
import GastBelevingPage from "./pages/GastBelevingPage";
import ReserveringenBezettingPage from "./pages/ReserveringenBezettingPage";
import MarketingActiesPage from "./pages/MarketingActiesPage";
import TrendsForecastingPage from "./pages/TrendsForecastingPage";
import KPIManagementPage from "./pages/KPIManagementPage";
import DoelenStructuurPage from "./pages/DoelenStructuurPage";
import ActiesEvaluatiePage from "./pages/ActiesEvaluatiePage";
import InstellingenPage from "./pages/InstellingenPage";
import ResearchPage from "./pages/ResearchPage";

// Protected Route wrapper for COMPANY users - includes subscription check
const ProtectedCompanyRoute = () => {
  const { user, loading } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => {
    return localStorage.getItem('sidebar-collapsed') === 'true';
  });

  // Listen for sidebar collapse changes
  React.useEffect(() => {
    const handleCollapseChange = (event) => {
      setSidebarCollapsed(event.detail);
    };
    
    window.addEventListener('sidebar-collapse-change', handleCollapseChange);
    
    return () => {
      window.removeEventListener('sidebar-collapse-change', handleCollapseChange);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#C2410C] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#57534E]">Laden...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admin users should go to admin dashboard, not company routes
  if (user.role === 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Company users need subscription check
  return (
    <SubscriptionGuard>
      <TrialBanner />
      <Sidebar />
      <main className={`main-content ${sidebarCollapsed ? 'sidebar-is-collapsed' : ''}`}>
        <Outlet />
      </main>
    </SubscriptionGuard>
  );
};

// Protected Route wrapper for ADMIN users only
const ProtectedAdminRoute = () => {
  const { user, loading } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => {
    return localStorage.getItem('sidebar-collapsed') === 'true';
  });

  // Listen for sidebar collapse changes
  React.useEffect(() => {
    const handleCollapseChange = (event) => {
      setSidebarCollapsed(event.detail);
    };
    
    window.addEventListener('sidebar-collapse-change', handleCollapseChange);
    
    return () => {
      window.removeEventListener('sidebar-collapse-change', handleCollapseChange);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#C2410C] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#57534E]">Laden...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Non-admin users should go to company dashboard
  if (user.role !== 'admin') {
    return <Navigate to="/company/dashboard" replace />;
  }

  // Admin users don't need subscription check
  return (
    <>
      <Sidebar />
      <main className={`main-content ${sidebarCollapsed ? 'sidebar-is-collapsed' : ''}`}>
        <Outlet />
      </main>
    </>
  );
};

// Permission-based route
const PermissionRoute = ({ permission, children }) => {
  const { hasPermission } = useAuth();

  if (permission && !hasPermission(permission)) {
    return <Navigate to="/company/dashboard" replace />;
  }

  return children;
};

// Helper function to get default route based on user role
const getDefaultRoute = (user) => {
  if (!user) return '/login';
  return user.role === 'admin' ? '/admin/dashboard' : '/company/dashboard';
};

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#C2410C] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#57534E]">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public reservation page - no auth required */}
        <Route path="/reserveren" element={<PublicReservationPage />} />
        
        {/* Public handbook page - password protected */}
        <Route path="/handboek" element={<PublicHandbookPage />} />
        
        {/* Public snelscan page - password protected */}
        <Route path="/snelscan/:shareId" element={<PublicSnelscanPage />} />
        
        {/* Public overview page - password protected */}
        <Route path="/bedrijfsoverzicht/:shareId" element={<PublicOverviewPage />} />

        {/* Public routes */}
        <Route 
          path="/login" 
          element={user ? <Navigate to={getDefaultRoute(user)} replace /> : <LoginPage />} 
        />
        
        {/* Account activation route */}
        <Route 
          path="/activate/:token" 
          element={<ActivatePage />} 
        />
        
        {/* Subscription page - requires login but not subscription (company users only) */}
        <Route 
          path="/company/subscription" 
          element={user ? (user.role === 'admin' ? <Navigate to="/admin/dashboard" replace /> : <SubscriptionPage />) : <Navigate to="/login" replace />} 
        />
        <Route 
          path="/company/subscription/success" 
          element={user ? (user.role === 'admin' ? <Navigate to="/admin/dashboard" replace /> : <SubscriptionPage />) : <Navigate to="/login" replace />} 
        />

        {/* Protected routes for COMPANY users */}
        <Route element={<ProtectedCompanyRoute />}>
          <Route path="/company/dashboard" element={<DashboardPage />} />
          
          <Route path="/company/omzet-verkoop" element={
            <PermissionRoute permission="omzet_verkoop">
              <OmzetVerkoopPage />
            </PermissionRoute>
          } />
          
          <Route path="/company/kosten-marges" element={
            <PermissionRoute permission="kosten_marges">
              <KostenMargesPage />
            </PermissionRoute>
          } />
          
          <Route path="/company/voorraad-inkoop" element={
            <PermissionRoute permission="voorraad_inkoop">
              <VoorraadInkoopPage />
            </PermissionRoute>
          } />
          
          <Route path="/company/personeel-planning" element={
            <PermissionRoute permission="personeel_planning">
              <PersoneelPlanningPage />
            </PermissionRoute>
          } />
          
          <Route path="/company/gast-beleving" element={
            <PermissionRoute permission="gast_beleving">
              <GastBelevingPage />
            </PermissionRoute>
          } />
          
          <Route path="/company/reserveringen-bezetting" element={
            <PermissionRoute permission="reserveringen_bezetting">
              <ReserveringenBezettingPage />
            </PermissionRoute>
          } />
          
          <Route path="/company/marketing-acties" element={
            <PermissionRoute permission="marketing_acties">
              <MarketingActiesPage />
            </PermissionRoute>
          } />
          
          <Route path="/company/trends-forecasting" element={
            <PermissionRoute permission="trends_forecasting">
              <TrendsForecastingPage />
            </PermissionRoute>
          } />
          
          <Route path="/company/kpi-management" element={
            <PermissionRoute permission="kpi_management">
              <KPIManagementPage />
            </PermissionRoute>
          } />
          
          <Route path="/company/doelen-structuur" element={
            <PermissionRoute permission="doelen_structuur">
              <DoelenStructuurPage />
            </PermissionRoute>
          } />
          
          <Route path="/company/acties-evaluatie" element={
            <PermissionRoute permission="acties_evaluatie">
              <ActiesEvaluatiePage />
            </PermissionRoute>
          } />
          
          <Route path="/company/research" element={<ResearchPage />} />
          
          <Route path="/company/instellingen" element={<InstellingenPage />} />
        </Route>

        {/* Protected routes for ADMIN users */}
        <Route element={<ProtectedAdminRoute />}>
          <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
          <Route path="/admin/companies" element={<AdminCompaniesPage />} />
          <Route path="/admin/subscriptions" element={<AdminSubscriptionsPage />} />
          <Route path="/admin/settings" element={<AdminSettingsPage />} />
        </Route>

        {/* Default redirect based on user role */}
        <Route path="*" element={<Navigate to={user ? getDefaultRoute(user) : "/login"} replace />} />
      </Routes>

      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#FFFFFF',
            border: '1px solid #E7E5E4',
            color: '#1C1917'
          }
        }}
      />
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
